import { formatElapsed, toast } from '@sentinel/web-core';
import { Icon } from '@sentinel/ui';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useActiveMission, useOpenMissions, useSelectMission } from '@/core/mission/active-mission';
import { MissionMenu } from './MissionMenu';

function useTicker(): number {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  return now;
}

/** The single active mission of the operator with its timer; one mission at a time. Its level
 *  is under the mark, not here (rule 4). */
export function MissionSelector() {
  const { t } = useTranslation();
  const { mission } = useActiveMission();
  const missions = useOpenMissions();
  const select = useSelectMission();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const now = useTicker();

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current !== null && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('click', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const choose = (id: string) => {
    setOpen(false);
    if (id === mission?.id) return;
    const chosen = missions.data?.items.find((m) => m.id === id);
    select.mutate(id, {
      onSuccess: () => {
        if (chosen !== undefined)
          toast(
            'info',
            t('shell.mission.switched', { name: chosen.name }),
            t('shell.mission.switchedDetail', { code: chosen.code }),
          );
      },
    });
  };

  return (
    <div className="msel" ref={ref}>
      <button
        type="button"
        className="msel-btn"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t('shell.mission.selectLabel')}
        onClick={() => setOpen((v) => !v)}
      >
        {mission === null ? (
          <span>
            <span className="nm">{t('shell.mission.none')}</span>
            <span className="cd">{t('shell.mission.noneHint')}</span>
          </span>
        ) : (
          <span>
            <span className="nm">{mission.name}</span>
            <span className="cd">
              {mission.code} · {mission.place.split(' · ')[0]?.toUpperCase()} ·{' '}
              {formatElapsed(mission.openedAt, now)}
            </span>
          </span>
        )}
        <Icon name="chevron" size={16} />
      </button>
      {open ? (
        <MissionMenu
          current={mission}
          missions={missions.data?.items ?? []}
          now={now}
          onChoose={choose}
          onLeave={() => setOpen(false)}
        />
      ) : null}
    </div>
  );
}
